import { useQuery } from '@tanstack/react-query';
import { brandsApi, companiesApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/lib/auth/useAuth';
import type { User, XkBrand } from '@/lib/api/types';

/**
 * Resolves the user's parent Company (the workspace). The Home page is
 * scoped to this entity — brands are its children.
 *
 * The backend does NOT expose `/co-companies/me`. Instead we read the
 * authenticated user's `companyId` from the JWT (which the auth store
 * already persists) and hit `GET /co-companies/:id` directly. Drop this
 * indirection when (if) the backend ships `/me`.
 */
export function useCurrentCompany() {
  const { companyId } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.companies.detail(companyId ?? 0),
    queryFn: () => companiesApi.get(companyId!),
    enabled: !!companyId,
    retry: false,
  });
  return { company: q.data ?? null, isLoading: q.isLoading };
}

/**
 * Brand managers for a company. The backend does NOT yet expose
 * `/co-companies/:id/managers` — the right shape would derive from
 * `/users?filter={companyId,managedBrandIds_nonNull}` once that's supported.
 *
 * Disabled by default to stop spamming 404s. The Brand managers card on the
 * dashboard already renders an empty state when `data?.items` is empty, so
 * this hook returns a static empty result.
 */
export function useCompanyManagers(_companyId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.companies.managers(_companyId ?? 0),
    queryFn: () => Promise.resolve({ items: [] as User[], total: 0 }),
    enabled: false,
    initialData: { items: [], total: 0 },
  });
}

/**
 * Resolves the "current" brand for the logged-in user.
 *
 * Today: first visible brand. Later: persisted selection from a brand
 * switcher in the TopBar. The hook centralises this so every page asks
 * the same question.
 */
export function useCurrentBrand() {
  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const brands = brandsQ.data?.items ?? [];
  const current: XkBrand | null = brands[0] ?? null;
  return {
    brand: current,
    brands,
    isLoading: brandsQ.isLoading,
    hasMultiple: brands.length > 1,
  };
}

export function useCompany(companyId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.companies.detail(companyId ?? 0),
    queryFn: () => brandsApi.get(companyId!),
    enabled: !!companyId,
  });
}
